/**
 * 파일 업로드 다이얼로그 — 파일 보관함과 현장 목록에서 함께 쓴다.
 *
 * 기존 업로드는 파일을 고르면 곧바로 순차 전송해서 (1) 무엇이 올라가는지 확인할
 * 틈이 없고 (2) 설명을 적을 자리가 없고 (3) 진행 상황이 "업로드 중…" 한 줄뿐이고
 * (4) 한 건 실패하면 전부 다시 골라야 했다. 이 다이얼로그는 올리기 전에 목록을
 * 만들어 두고, 파일별로 설명·분류를 적고, 병렬로 올리며 실패한 건만 재시도한다.
 *
 * projectId 를 주면 그 현장에 고정되고, 주지 않으면 현장 선택 UI 가 나온다
 * (로그인 직후 현장 목록에서 바로 올리는 경로).
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  LinearProgress,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  CloudUpload as CloudUploadIcon,
  Close as CloseIcon,
  Delete as DeleteIcon,
  CheckCircle as CheckCircleIcon,
  ErrorOutline as ErrorIcon,
  Replay as ReplayIcon,
  AddCircleOutline as AddIcon,
  FolderOpen as FolderIcon,
} from '@mui/icons-material';
import RecordKindSelector, {
  RecordKind,
  RecordKindHint,
} from '../RecordKindSelector';
import { SectionLabel, stickyActionsSx } from '../formParts';
import {
  DOCUMENT_CATEGORIES,
  DOCUMENT_CATEGORY_LABEL,
} from '../../../models/SiteDocument';
import { SiteProject } from '../../../models/SiteProject';
import { SiteProjectService } from '../../../services/SiteProjectService';
import { SiteDocumentService } from '../../../services/SiteDocumentService';
import { documentFileVisual, formatDocumentSize } from './fileVisual';
import {
  documentBaseName,
  documentFolder,
  filesFromDrop,
  filesFromInput,
} from './folderFiles';
import { DEFAULT_UPLOAD_CONCURRENCY, runWithConcurrency } from './uploadQueue';

type StagedStatus = 'ready' | 'uploading' | 'done' | 'error';

interface StagedFile {
  /** File 객체는 같은 파일을 다시 골라도 새 인스턴스라 별도 키가 필요하다. */
  key: string;
  file: File;
  description: string;
  category: string;
  status: StagedStatus;
  progress: number;
  error?: string;
}

export interface DocumentUploadDialogProps {
  open: boolean;
  onClose: () => void;
  /**
   * 업로드 대상 현장. 지정하면 선택 UI 를 감춘다.
   * 미지정이면 사용자가 고르게 하며, 고르기 전에는 업로드할 수 없다.
   */
  projectId?: number;
  /** 표시용 현장 이름 (projectId 고정일 때 헤더에 보여 준다). */
  projectLabel?: string;
  /** 최소 1건이라도 성공했을 때 호출 — 호출 측이 목록을 다시 읽는다. */
  onUploaded?: (successCount: number) => void;
  /**
   * 열면서 미리 담아 둘 파일 — 페이지에 파일을 끌어다 놓아 여는 경로.
   * 예전 동작(놓으면 바로 업로드)과 달리 설명·분류를 적을 기회를 준다.
   */
  initialFiles?: File[];
  /**
   * 현장 선택 모드에서 미리 골라 둘 현장 id — 다른 등록 종류에서 옮겨올 때
   * 이미 고른 현장을 다시 고르게 만들지 않는다.
   */
  initialSiteId?: number;
  /** 최상단 종류 선택기에 노출할 선택지. 2개 이상일 때만 선택기가 보인다. */
  availableKinds?: RecordKind[];
  /** 파일이 아닌 종류가 선택됐을 때 부모(통합 등록 진입점)에게 넘긴다. */
  onKindChange?: (kind: RecordKind) => void;
}

let stagedSeq = 0;
const nextKey = (): string => {
  stagedSeq += 1;
  return `staged-${stagedSeq}`;
};

const siteLabel = (p: SiteProject): string => p.name_ko?.trim() || p.name;

const DocumentUploadDialog: React.FC<DocumentUploadDialogProps> = ({
  open,
  onClose,
  projectId,
  projectLabel,
  onUploaded,
  initialFiles,
  initialSiteId,
  availableKinds,
  onKindChange,
}) => {
  const theme = useTheme();
  // 좁은 화면의 모달은 좌우 여백만 먹고 파일 목록이 들어갈 자리가 좁아진다.
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const fixedProject = typeof projectId === 'number' && Number.isFinite(projectId);

  const [files, setFiles] = useState<StagedFile[]>([]);
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  // 폴더 선택은 같은 input 으로 겸할 수 없다 — webkitdirectory 가 붙으면 낱개
  // 파일을 고를 수 없어서 두 개를 따로 둔다.
  const folderInputRef = useRef<HTMLInputElement | null>(null);

  // 현장 선택 모드에서만 목록을 불러온다.
  const [sites, setSites] = useState<SiteProject[]>([]);
  const [sitesLoading, setSitesLoading] = useState(false);
  const [targetSite, setTargetSite] = useState<SiteProject | null>(null);

  useEffect(() => {
    if (!open || fixedProject) return;
    let alive = true;
    setSitesLoading(true);
    SiteProjectService.listProjects()
      .then((list) => {
        if (!alive) return;
        setSites(list);
        // 옮겨오면서 이미 고른 현장이 있으면 그대로 잇는다.
        if (initialSiteId != null) {
          const found = list.find((p) => p.id === initialSiteId);
          if (found) setTargetSite(found);
        }
      })
      .catch((e) => {
        if (alive) setError(e instanceof Error ? e.message : '현장 목록을 불러오지 못했습니다');
      })
      .finally(() => {
        if (alive) setSitesLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [open, fixedProject, initialSiteId]);

  // 끌어다 놓아 열린 경우 그 파일들을 담고 시작한다. open 전환 시점에만 반응하도록
  // initialFiles 를 의존성에서 빼면 stale 참조가 되므로 길이·이름으로 식별한다.
  const initialKey = (initialFiles || []).map((f) => `${f.name}:${f.size}`).join('|');
  useEffect(() => {
    if (!open || !initialFiles || initialFiles.length === 0) return;
    setFiles(
      initialFiles.map((file) => ({
        key: nextKey(),
        file,
        description: '',
        category: 'other',
        status: 'ready' as StagedStatus,
        progress: 0,
      })),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialKey]);

  // 닫을 때 초기화 — 다음에 열었을 때 지난번 목록이 남아 있으면 실수로 재업로드한다.
  useEffect(() => {
    if (open) return;
    setFiles([]);
    setUploading(false);
    setError(null);
    setDragActive(false);
    setTargetSite(null);
  }, [open]);

  const effectiveProjectId = fixedProject ? (projectId as number) : targetSite?.id;

  const addFiles = useCallback(
    (incoming: FileList | File[]) => {
      const arr = Array.from(incoming);
      if (arr.length === 0) return;
      setFiles((prev) => [
        ...prev,
        ...arr.map((file) => ({
          key: nextKey(),
          file,
          description: '',
          category: 'other',
          status: 'ready' as StagedStatus,
          progress: 0,
        })),
      ]);
      setError(null);
    },
    [],
  );

  const patch = useCallback((key: string, changes: Partial<StagedFile>) => {
    setFiles((prev) => prev.map((f) => (f.key === key ? { ...f, ...changes } : f)));
  }, []);

  const remove = useCallback((key: string) => {
    setFiles((prev) => prev.filter((f) => f.key !== key));
  }, []);

  const pending = useMemo(() => files.filter((f) => f.status === 'ready' || f.status === 'error'), [files]);
  const doneCount = files.filter((f) => f.status === 'done').length;
  const failedCount = files.filter((f) => f.status === 'error').length;

  const startUpload = useCallback(async () => {
    if (effectiveProjectId === undefined) {
      setError('업로드할 현장을 먼저 선택하세요');
      return;
    }
    const targets = files.filter((f) => f.status === 'ready' || f.status === 'error');
    if (targets.length === 0) return;

    setUploading(true);
    setError(null);
    // 재시도 시 이전 오류 표시를 지운다.
    setFiles((prev) =>
      prev.map((f) =>
        f.status === 'ready' || f.status === 'error'
          ? { ...f, status: 'uploading', progress: 0, error: undefined }
          : f,
      ),
    );

    let success = 0;
    await runWithConcurrency(targets, DEFAULT_UPLOAD_CONCURRENCY, async (item) => {
      try {
        await SiteDocumentService.upload(effectiveProjectId, item.file, {
          description: item.description.trim() || undefined,
          category: item.category,
          onProgress: (percent) => patch(item.key, { progress: percent }),
        });
        success += 1;
        patch(item.key, { status: 'done', progress: 100 });
      } catch (e) {
        patch(item.key, {
          status: 'error',
          error: e instanceof Error ? e.message : '업로드 실패',
        });
      }
    });

    setUploading(false);
    if (success > 0) onUploaded?.(success);
    // 전부 성공했으면 닫는다 — 확인만 하고 닫는 동작을 매번 손으로 반복해야 했다.
    // 실패가 남아 있으면 무엇이 실패했는지 보고 재시도해야 하므로 열어 둔다.
    if (success === targets.length) onClose();
  }, [effectiveProjectId, files, patch, onUploaded, onClose]);

  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragActive(false);
    if (uploading) return;
    // 폴더째 놓을 수 있어야 하므로 dataTransfer.files 가 아니라 항목을 훑는다.
    filesFromDrop(e.dataTransfer).then(addFiles);
  };

  return (
    <Dialog
      open={open}
      onClose={uploading ? undefined : onClose}
      maxWidth="md"
      fullWidth
      fullScreen={isMobile}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          pr: 1,
          // 전체 화면에서 파일 목록을 스크롤해도 제목/닫기가 남아 있어야 한다.
          position: 'sticky',
          top: 0,
          zIndex: 2,
          bgcolor: 'background.paper',
        }}
      >
        <CloudUploadIcon color="primary" />
        <Stack sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="h6" fontWeight={700} component="span">
            파일 업로드
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {fixedProject
              ? `${projectLabel || '현재 현장'} 보관함에 올립니다 · 여러 개를 한 번에 올릴 수 있습니다`
              : '올릴 현장을 고르고 파일을 추가하세요'}
          </Typography>
        </Stack>
        <IconButton onClick={onClose} disabled={uploading} aria-label="닫기">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ px: { xs: 2, sm: 3 } }}>
        <Stack spacing={2}>
          {/* 종류 — 통합 등록 진입점으로 열렸을 때만. 일정/메모 폼과 같은 자리. */}
          {onKindChange && (availableKinds?.length ?? 0) > 1 && (
            <Box>
              <SectionLabel>종류</SectionLabel>
              <RecordKindSelector
                value="file"
                kinds={availableKinds}
                onChange={(k) => k !== 'file' && onKindChange(k)}
                disabled={uploading}
              />
              <RecordKindHint kind="file" />
            </Box>
          )}

          {error && (
            <Alert severity="error" onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {/* 현장 선택 — projectId 가 고정되지 않은 진입점(현장 목록)에서만. */}
          {!fixedProject && (
            <Autocomplete
              options={sites}
              value={targetSite}
              onChange={(_, v) => {
                setTargetSite(v);
                setError(null);
              }}
              getOptionLabel={siteLabel}
              isOptionEqualToValue={(a, b) => a.id === b.id}
              loading={sitesLoading}
              disabled={uploading}
              renderOption={(props, option) => (
                <li {...props} key={option.id}>
                  <Stack>
                    <Typography variant="body2" fontWeight={600}>
                      {siteLabel(option)}
                    </Typography>
                    {option.location && (
                      <Typography variant="caption" color="text.secondary">
                        {option.location}
                      </Typography>
                    )}
                  </Stack>
                </li>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="업로드할 현장"
                  required
                  placeholder="현장 이름으로 검색"
                  helperText="파일은 선택한 현장의 보관함에만 저장됩니다"
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {sitesLoading ? <CircularProgress size={16} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
          )}

          {/* 드롭 영역 */}
          <Box
            onDragOver={(e) => {
              e.preventDefault();
              if (!uploading) setDragActive(true);
            }}
            onDragLeave={() => setDragActive(false)}
            onDrop={onDrop}
            onClick={() => !uploading && fileInputRef.current?.click()}
            sx={{
              border: '2px dashed',
              borderColor: dragActive ? 'primary.main' : 'divider',
              bgcolor: dragActive ? 'action.hover' : 'transparent',
              borderRadius: 2,
              py: files.length === 0 ? 5 : 2.5,
              textAlign: 'center',
              cursor: uploading ? 'default' : 'pointer',
              transition: 'all .15s',
            }}
          >
            <AddIcon color={dragActive ? 'primary' : 'disabled'} />
            <Typography variant="body2" sx={{ mt: 0.5 }}>
              파일·폴더를 끌어다 놓거나 클릭해서 선택
            </Typography>
            <Typography variant="caption" color="text.secondary">
              형식 제한 없음 · 파일당 최대 1GB · 폴더는 안쪽 파일까지 담깁니다
            </Typography>
            <Box sx={{ mt: 1 }}>
              <Button
                size="small"
                startIcon={<FolderIcon />}
                disabled={uploading}
                onClick={(e) => {
                  // 드롭 영역 자체의 onClick(파일 선택)이 같이 열리지 않게 한다.
                  e.stopPropagation();
                  folderInputRef.current?.click();
                }}
                sx={{ textTransform: 'none' }}
              >
                폴더 선택
              </Button>
            </Box>
          </Box>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            hidden
            data-testid="document-file-input"
            onChange={(e) => {
              addFiles(filesFromInput(e.target.files));
              e.target.value = '';
            }}
          />
          <input
            ref={folderInputRef}
            type="file"
            multiple
            hidden
            data-testid="document-folder-input"
            // webkitdirectory 는 표준 타입 정의에 없다. React 는 모르는 속성을
            // 그대로 DOM 에 넘기므로 문자열로 준다.
            {...({ webkitdirectory: '', directory: '' } as Record<string, string>)}
            onChange={(e) => {
              addFiles(filesFromInput(e.target.files));
              e.target.value = '';
            }}
          />

          {/* 스테이징 목록 */}
          {files.length > 0 && (
            <>
              <Divider />
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                sx={{ flexWrap: 'wrap', rowGap: 0.5 }}
              >
                <Typography variant="subtitle2" fontWeight={700}>
                  올릴 파일 {files.length}개
                </Typography>
                <Stack direction="row" spacing={0.5}>
                  {doneCount > 0 && <Chip size="small" color="success" label={`완료 ${doneCount}`} />}
                  {failedCount > 0 && <Chip size="small" color="error" label={`실패 ${failedCount}`} />}
                </Stack>
              </Stack>

              <Stack spacing={1.25}>
                {files.map((f) => {
                  const { Icon, color } = documentFileVisual(f.file.name, f.file.type);
                  // 폴더 업로드면 이름이 "폴더/하위/파일.ext" 다. 그대로 한 줄에
                  // 넣으면 말줄임이 정작 파일명을 지운다 — 경로는 아래 줄로 뺀다.
                  const folder = documentFolder(f.file.name);
                  const baseName = documentBaseName(f.file.name);
                  return (
                    <Box
                      key={f.key}
                      sx={{
                        border: '1px solid',
                        borderColor: f.status === 'error' ? 'error.light' : 'divider',
                        borderRadius: 2,
                        p: 1.5,
                        bgcolor: f.status === 'done' ? 'action.hover' : 'background.paper',
                      }}
                    >
                      <Stack direction="row" spacing={1.5} alignItems="flex-start">
                        <Icon sx={{ color, mt: 0.5, flexShrink: 0 }} />
                        <Stack sx={{ flex: 1, minWidth: 0 }} spacing={1}>
                          <Stack direction="row" alignItems="center" spacing={1}>
                            <Tooltip title={f.file.name}>
                              <Typography
                                variant="body2"
                                fontWeight={600}
                                sx={{
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                  flex: 1,
                                  minWidth: 0,
                                }}
                              >
                                {baseName}
                              </Typography>
                            </Tooltip>
                            <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
                              {formatDocumentSize(f.file.size)}
                            </Typography>
                            {f.status === 'done' && <CheckCircleIcon color="success" fontSize="small" />}
                            {f.status === 'error' && <ErrorIcon color="error" fontSize="small" />}
                            {f.status !== 'uploading' && (
                              <Tooltip title="목록에서 제거">
                                {/* my 음수 보정: 버튼 패딩이 행 높이를 키워 파일명이
                                    출렁이지 않게 한다. 파일명과 세로 중앙 정렬. */}
                                <IconButton
                                  size="small"
                                  onClick={() => remove(f.key)}
                                  aria-label={`${f.file.name} 제거`}
                                  sx={{ flexShrink: 0, my: -0.5 }}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Stack>

                          {folder && (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ mt: -0.5, wordBreak: 'break-all' }}
                            >
                              📁 {folder}
                            </Typography>
                          )}

                          {/* 설명·분류는 업로드 전/실패 상태에서만 편집 가능 */}
                          {f.status !== 'done' && (
                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                              <TextField
                                size="small"
                                fullWidth
                                label="설명"
                                placeholder="어떤 파일인지 적어 주세요 (다른 사람이 보고 판단합니다)"
                                value={f.description}
                                onChange={(e) => patch(f.key, { description: e.target.value })}
                                disabled={uploading}
                                inputProps={{ 'aria-label': `${f.file.name} 설명` }}
                              />
                              <TextField
                                select
                                size="small"
                                label="분류"
                                value={f.category}
                                onChange={(e) => patch(f.key, { category: e.target.value })}
                                disabled={uploading}
                                sx={{ minWidth: 150 }}
                              >
                                {DOCUMENT_CATEGORIES.map((c) => (
                                  <MenuItem key={c} value={c}>
                                    {DOCUMENT_CATEGORY_LABEL[c]}
                                  </MenuItem>
                                ))}
                              </TextField>
                            </Stack>
                          )}

                          {f.status === 'done' && f.description && (
                            <Typography variant="caption" color="text.secondary">
                              {f.description}
                            </Typography>
                          )}

                          {f.status === 'uploading' && (
                            <LinearProgress
                              variant="determinate"
                              value={f.progress}
                              sx={{ height: 6, borderRadius: 3 }}
                            />
                          )}
                          {f.status === 'error' && (
                            <Typography variant="caption" color="error">
                              {f.error}
                            </Typography>
                          )}
                        </Stack>

                      </Stack>
                    </Box>
                  );
                })}
              </Stack>
            </>
          )}
        </Stack>
      </DialogContent>

      <DialogActions sx={stickyActionsSx}>
        {failedCount > 0 && !uploading && (
          <Typography variant="caption" color="error" sx={{ mr: 'auto' }}>
            실패한 {failedCount}개만 다시 시도합니다
          </Typography>
        )}
        <Button onClick={onClose} disabled={uploading} sx={{ textTransform: 'none' }}>
          {doneCount > 0 ? '닫기' : '취소'}
        </Button>
        <Button
          variant="contained"
          onClick={startUpload}
          disabled={uploading || pending.length === 0 || effectiveProjectId === undefined}
          startIcon={
            uploading ? (
              <CircularProgress size={16} color="inherit" />
            ) : failedCount > 0 ? (
              <ReplayIcon />
            ) : (
              <CloudUploadIcon />
            )
          }
          sx={{ textTransform: 'none', borderRadius: 2 }}
        >
          {uploading
            ? '업로드 중…'
            : failedCount > 0
              ? `${failedCount}개 재시도`
              : `${pending.length}개 업로드`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DocumentUploadDialog;
